/**
 * Step configuration constants
 */

export const STEP_NAMES = {
  GENERAL: 'General',
  CONNECTION: 'Connection',
  CREATE_ACCOUNT: 'CreateAccount',
  ADD_ACCESS: 'AddAccess',
  UPDATE_ACCOUNT: 'UpdateAccount',
  ENABLE_ACCOUNT: 'EnableAccount',
  DISABLE_ACCOUNT: 'DisableAccount',
  REMOVE_ACCOUNT: 'RemoveAccount',
  REMOVE_ACCESS: 'RemoveAccess',
  CONFIRMATION: 'Confirmation',
} as const;

export const STEP_KEYS = {
  GENERAL: 'GENERAL',
  CONNECTION: 'CONNECTION',
  CREATE_ACCOUNT: 'CREATE_ACCOUNT',
  ADD_ACCESS: 'ADD_ACCESS',
  REMOVE_ACCESS: 'REMOVE_ACCESS',
  UPDATE_ACCOUNT: 'UPDATE_ACCOUNT',
  ENABLE_ACCOUNT: 'ENABLE_ACCOUNT',
  DISABLE_ACCOUNT: 'DISABLE_ACCOUNT',
  REMOVE_ACCOUNT: 'REMOVE_ACCOUNT',
  CONFIRMATION: 'CONFIRMATION',
} as const;

export const NAVIGATION_STEPS = [
  { key: STEP_KEYS.GENERAL, label: 'Application Details' },
  { key: STEP_KEYS.CONNECTION, label: 'Connection Setup' },
  { key: STEP_KEYS.CREATE_ACCOUNT, label: 'Create Account' },
  { key: STEP_KEYS.UPDATE_ACCOUNT, label: 'Update Account' },
  { key: STEP_KEYS.ENABLE_ACCOUNT, label: 'Enable Account' },
  { key: STEP_KEYS.DISABLE_ACCOUNT, label: 'Disable Account' },
  { key: STEP_KEYS.REMOVE_ACCOUNT, label: 'Remove Account' },
  { key: STEP_KEYS.ADD_ACCESS, label: 'Add Access' },
  { key: STEP_KEYS.REMOVE_ACCESS, label: 'Remove Access' },
] as const;

export const DEFAULT_VISITED_STEPS = ['General'] as const;

export const AUTO_SCROLL_INTERVAL = 500;
export const MAX_INPUT_HEIGHT_PX = 116;
export const CHARACTER_LIMIT = 2000;
export const CHARACTER_COUNT_THRESHOLD = 200;

// UI Modes
export const UI_MODES = {
  CREATE: 'create',
  EDIT: 'edit',
} as const;

// Timeout Values (in milliseconds)
export const TIMEOUTS = {
  GENERAL_FORM_DELAY: 2000,
  TRANSITION_MESSAGE_DURATION: 3000,
} as const;

// URL Parameters
export const URL_PARAMS = {
  ENDPOINT_KEY: 'endpointKey',
  IS_EDIT: 'isEdit',
} as const;

// URL Parameter Values
export const URL_PARAM_VALUES = {
  TRUE: 'true',
} as const;

// Message Types
export const MESSAGE_TYPES = {
  AI_RESPONSE: 'ai_response',
  YES_NO: 'yes_no',
  STEP_FORM: 'step_form',
  FILE_PROMPT: 'file_prompt',
  USER_INPUT: 'user_input',
  USER_RESPONSE: 'user_response',
  ERRROR: 'error',
} as const;

// Message SubTypes
export const MESSAGE_SUBTYPES = {
  // AI Response subtypes (SnippetType from Copilot1)
  CREATE_ACCOUNT: 'createAccount',
  UPDATE_ACCOUNT: 'updateAccount',
  ENABLE_ACCOUNT: 'enableAccount',
  DISABLE_ACCOUNT: 'disableAccount',
  REMOVE_ACCOUNT: 'removeAccount',
  FILE_EXTRACTION: 'fileExtraction',
  SUCCESS: 'success',
  ERROR: 'error',
  ANALYZING: 'analyzing',
  MOVING_NEXT: 'movingNext',
  GENERAL_TO_CONNECTION: 'generalToConnection',
  START: 'start',
  CREATE_ACCOUNT_HELPER_TEXT: 'createAccountHelperText',
  PROVIDE_ADDITIONAL_INFO: 'provideAdditionalInfo',
  MOVING_TO_NEXT_STEP: 'movingToNextStep',
  MOVING_TO_NEXT_ACC_STEP: 'movingToNextAccStep',
  MOVING_TO_NEXT_WITH_SKIP: 'movingToNextStepWithSkip',
  SKIP_TRANSITION: 'skipTransition',

  // Step Form subtypes
  BASIC_CONFIG: 'basic_config',
  CONNECTION_CONFIG: 'connection_config',
  CREATE_ACCOUNT_FORM: 'create_account_form',
  ADD_ACCESS_FORM: 'add_access_form',
  REMOVE_ACCESS_FORM: 'remove_access_form',
  UPDATE_ACCOUNT_FORM: 'update_account_form',
  ENABLE_ACCOUNT_FORM: 'enable_account_form',
  DISABLE_ACCOUNT_FORM: 'disable_account_form',
  REMOVE_ACCOUNT_FORM: 'remove_account_form',

  // User Input subtypes
  TEXT_INPUT: 'text_input',
  FILE_UPLOAD: 'file_upload',

  // Yes/No subtypes
  USER_CHOICE: 'user_choice',
  USER_RESPONSE_YES: 'userResponseYes',
  USER_RESPONSE_NO: 'userResponseNo',
  INSTRUCTION_PROMPT: 'instruction_prompt',

  // File Prompt subtypes
  FILE_DISPLAY: 'file_display',

  // Connection Step
  CONNECTION_STEP: 'connectionStep',
  CONNECTION_STEP_ASSIST_INFO: 'connectionStepAssistInfo',
  SELECT_ONE_OF_OPTIONS: 'selectOneOfOptions',
  CONNECTION_SUCCESS_MOVING_NEXT: 'connectionSuccessMovingNext',
  CREATE_ACCOUNT_NEXT_STEP: 'createAccountNextStep',
  CREATE_ACCOUNT_ASSIST: 'createAccountAssist',
  ADDITIONAL_INFORMATION: 'additionalInformation',
  CONFIRM_ONBOARD: 'confirmOnboard',
  PROCEED_TO_UPDATE_ACCOUNT: 'proceedToUpdateAccount',
  UPDATE_ACCOUNT_STEP_ASSIST_INFO: 'updateAccountStepAssistInfo',
  UPDATE_ACCOUNT_CONFIRMATION_INFO: 'updateAccountConfirmationInfo',
  ACCOUNT_CONFIRMATION_INFO: 'allAccountGenericConfirmation',
  ENABLE_ACCOUNT_STEP_ASSIST_INFO: 'enableAccountStepAssistInfo',
  DISABLE_ACCOUNT_STEP_ASSIST_INFO: 'disableAccountStepAssistInfo',
  REMOVE_ACCOUNT_STEP_ASSIST_INFO: 'removeAccountStepAssistInfo',
  PROCEED_TO_REMOVE_ACCOUNT: 'proceedToRemoveAccount',
  PROCEED_TO_ENABLE_ACCOUNT: 'proceedToEnableAccount',
  PROCEED_TO_DISABLE_ACCOUNT: 'proceedToDisableAccount',
  FINAL_RECOMMENDATIONS: 'finalRecommendations',

  // Add Access
  MOVING_TO_ACCESS_ACCOUNT: 'movingToAccessAccount',
  ACCESS_ACCOUNT_MESSAGE: 'accessAccountMessage',
  ACCESS_ATTRIBUTE_MAPPING: 'accessAttributeMapping',
  REMOVE_ACCESS_ATTRIBUTE_MAPPING: 'removeAccess',
  ATTRIBUTE_MAPPING_APPLICATION_FIELDS: 'attributeMappingApplicationFields',
  ATTRIBUTE_MAPPING_START: 'attributeMappingStart',
  ATTRIBUTE_MAPPING_NEXT: 'attributeMappingNext',
  ATTRIBUTE_MAPPING_ADDITIONAL_INFO: 'attributeMappingAdditionalInfo',
  ADD_ACCESS_EDIT_PHASE: 'addAccessEditPhase',
  ALL_ENTITLEMENT_DONE: 'allEntitlementDone',
  ADD_ACCESS_INFO_EDIT_MODE: 'addAccessInfoEditMode',

  // Remove Access
  REMOVE_ACCESS_NEXT_STEP: 'removeAccessNextStep',
  REMOVE_ACCESS_MAPPING: 'removeAccessMapping',
  ALL_ACCOUNT_GENERIC_CONFIRMATION: 'allAccountGenericConfirmation',
  ENABLE_ACCOUNT_GENERIC_CONFIRMATION: 'enableAccountGenericConfirmation',
  DISABLE_ACCOUNT_GENERIC_CONFIRMATION: 'disableAccountGenericConfirmation',
  REMOVE_ACCOUNT_GENERIC_CONFIRMATION: 'removeAccountGenericConfirmation',
  REMOVE_ACCESS_MAPPING_INFO: 'removeAccessMappingInfo',
  REDIRECTION_ON_CONFIRMATION: 'finalConfirmationRedirection',
  REMOVE_ACCESS_EDIT_INFO: 'removeAccessEditInfo',
  REMOVE_ACCESS_SKIPPED: 'removeAccessSkipped',

  // Edit Flow Success
  BASIC_UPDATE_SUCCESS: 'basicUpdateSuccess',
  CREATE_ACCOUNT_UPDATE_SUCCESS: 'createAccountUpdateSuccess',
  UPDATE_ACCOUNT_UPDATE_SUCCESS: 'updateAccountUpdateSuccess',
  ENABLE_ACCOUNT_UPDATE_SUCCESS: 'enableAccountUpdateSuccess',
  DISABLE_ACCOUNT_UPDATE_SUCCESS: 'disableAccountUpdateSuccess',
  REMOVE_ACCOUNT_UPDATE_SUCCESS: 'removeAccountUpdateSuccess',
  ADD_ACCESS_UPDATE_SUCCESS: 'addAccessUpdateSuccess',
  REMOVE_ACCESS_UPDATE_SUCCESS: 'removeAccessUpdateSuccess',

  // Previous Step Edit Messages
  PROMPT_INSTRUCTIONS: 'promptInstructions',
  STEP_HELPER_PROMPT: 'stepHelperPrompt',
  FINAL_CONFIRMATION: 'finalConfirmation',
  THREE_BUTTONS_HELPER_TEXT: 'threeButtonsHelperText',
} as const;

export const SEE_MORE_ON_REDIRECTION = '/design/catalogManagement/CUA/';
